import {
  type AiCanvas,
  aiSceneInput,
  buildScene,
  GUIDE_COLOR,
  guidance,
  type LightStyle,
  type Run,
  type TimePreset,
} from '@better-lights/core';

/**
 * AI input images (docs/spec/03 §8), each `canvas.width × canvas.height` with the photo in
 * `canvas.content` (black letterbox): photo (JPEG q90), guide = photo + magenta runs and a dot
 * at every LED (JPEG q90), and for day renders an edit mask (PNG; transparent = editable).
 */
export interface AiImages {
  photo: Blob;
  guide: Blob | null;
  mask: Blob | null;
}

export interface AiImageInput {
  photo: ImageBitmap;
  canvas: AiCanvas;
  time: TimePreset;
  runs: Run[];
  style: LightStyle;
  colorId: string;
  customHex: string;
  frameWidthFt: number;
}

function surface(c: AiCanvas): [OffscreenCanvas, OffscreenCanvasRenderingContext2D] {
  const canvas = new OffscreenCanvas(c.width, c.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(`Could not create a ${c.width}×${c.height} drawing surface.`);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  return [canvas, ctx];
}

function drawPhoto(ctx: OffscreenCanvasRenderingContext2D, input: AiImageInput): void {
  const { content } = input.canvas;
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, input.canvas.width, input.canvas.height);
  ctx.drawImage(input.photo, content.x, content.y, content.width, content.height);
}

export async function buildAiImages(input: AiImageInput): Promise<AiImages> {
  const { canvas: c } = input;
  const { hasGuide, hasMask } = guidance({ runs: input.runs, time: input.time });
  const [pc, pctx] = surface(c);
  drawPhoto(pctx, input);
  const photo = await pc.convertToBlob({ type: 'image/jpeg', quality: 0.9 });
  if (!hasGuide) return { photo, guide: null, mask: null };

  const scene = buildScene(
    aiSceneInput(c, {
      runs: input.runs,
      style: input.style,
      colorId: input.colorId,
      customHex: input.customHex,
      time: input.time,
      frameWidthFt: input.frameWidthFt,
    }),
  );
  const Wc = c.content.width;
  const [gc, g] = surface(c);
  drawPhoto(g, input);
  g.save();
  g.beginPath();
  g.rect(c.content.x, c.content.y, c.content.width, c.content.height);
  g.clip();
  g.translate(c.content.x, c.content.y);
  const w = Math.max(2, Wc * 0.0035);
  g.strokeStyle = GUIDE_COLOR;
  g.fillStyle = GUIDE_COLOR;
  g.lineWidth = w;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  for (const run of scene.runs) {
    g.beginPath();
    run.pts.forEach((p, i) => {
      if (i === 0) g.moveTo(p.x, p.y);
      else g.lineTo(p.x, p.y);
    });
    g.stroke();
    for (const led of run.lights) {
      g.beginPath();
      g.arc(led.x, led.y, 1.25 * w, 0, Math.PI * 2);
      g.fill();
    }
  }
  g.restore();
  const guide = await gc.convertToBlob({ type: 'image/jpeg', quality: 0.9 });
  if (!hasMask) return { photo, guide, mask: null };

  const [mc, m] = surface(c);
  m.fillStyle = '#000000';
  m.fillRect(0, 0, c.width, c.height);
  m.save();
  m.beginPath();
  m.rect(c.content.x, c.content.y, c.content.width, c.content.height);
  m.clip();
  m.translate(c.content.x, c.content.y);
  m.globalCompositeOperation = 'destination-out';
  m.lineWidth = Wc * 0.03;
  m.lineCap = 'round';
  m.lineJoin = 'round';
  m.strokeStyle = '#000000';
  const reach = Math.max(Wc * 0.02, scene.washLength);
  for (let i = 0; i <= 6; i++) {
    const dy = (reach * i) / 6;
    for (const run of scene.runs) {
      m.beginPath();
      run.pts.forEach((p, k) => {
        if (k === 0) m.moveTo(p.x, p.y + dy);
        else m.lineTo(p.x, p.y + dy);
      });
      m.stroke();
    }
  }
  m.restore();
  const mask = await mc.convertToBlob({ type: 'image/png' });
  return { photo, guide, mask };
}
